import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, UserFormData, UserRole, getRoleCategory, isClinicalRole } from '../types/user'
import { USER_SALUTATIONS } from '../types/salutation'
import { getAddressLabels, getDefaultCountry } from '../utils/locale'
import { userService } from '../services/userService'
import './UserForm.css'

// Base schema without role-based validation
const baseUserSchema = z.object({
  username: z
    .string()
    .min(1, 'Username is required')
    .min(3, 'Username must be at least 3 characters'),
  email: z.string().email('Invalid email address').min(1, 'Email is required'),
  salutation: z
    .enum(['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof', 'Rev'])
    .optional(),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters'),
  middleName: z.string().min(2, 'Middle name must be at least 2 characters').optional().or(z.literal('')),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  role: z.enum([
    'admin', 'doctor', 'nurse', 'receptionist', 'physician-assistant', 'therapist',
    'billing', 'office-manager', 'system-admin', 'it-support'
  ]),
  roleCategory: z.enum(['Administrative', 'Clinical', 'System']),
  password: z.string().min(8, 'Password must be at least 8 characters').optional().or(z.literal('')),
  isActive: z.boolean(),
  twoFactorEnabled: z.boolean().optional(),
  // Contact information
  phone: z.string().regex(/^[\d\s\-\+\(\)]+$/, 'Invalid phone number').optional().or(z.literal('')),
  mobile: z.string().regex(/^[\d\s\-\+\(\)]+$/, 'Invalid mobile number').optional().or(z.literal('')),
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
  }).optional(),
  // Professional information
  hpi: z.string().regex(/^[A-Z0-9]+$/, 'HPI must be alphanumeric').optional().or(z.literal('')),
  professionalBodyRegistrationNumber: z.string().optional().or(z.literal('')),
  prescriberIdentifierNumber: z.string().optional().or(z.literal('')),
  gmsNumber: z.string().optional().or(z.literal('')),
  // Signature is uploaded separately via S3, not part of form data
  // Contractual arrangements
  contractualArrangements: z.array(z.object({
    type: z.enum(['full-time', 'part-time']),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().optional(),
  })).optional(),
})

// Schema with role-based validation
const userSchema = baseUserSchema.superRefine((data, ctx) => {
  const role = data.role as UserRole
  const isClinical = isClinicalRole(role)
  
  // Professional body registration number required for clinical roles
  if (isClinical && (!data.professionalBodyRegistrationNumber || data.professionalBodyRegistrationNumber.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Professional body registration number is required for clinical roles',
      path: ['professionalBodyRegistrationNumber'],
    })
  }
  
  // Prescriber identifier number required for nurses
  if (role === 'nurse' && (!data.prescriberIdentifierNumber || data.prescriberIdentifierNumber.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Prescriber identifier number is required for nurses',
      path: ['prescriberIdentifierNumber'],
    })
  }
  
  // GMS number required for doctors
  if (role === 'doctor' && (!data.gmsNumber || data.gmsNumber.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'GMS number is required for doctors',
      path: ['gmsNumber'],
    })
  }
  
  // Contractual arrangements required for all roles
  if (!data.contractualArrangements || data.contractualArrangements.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'At least one contractual arrangement is required',
      path: ['contractualArrangements'],
    })
  }
  
  // Validate end date is after start date if provided
  if (data.contractualArrangements) {
    data.contractualArrangements.forEach((arrangement, index) => {
      if (arrangement.endDate && arrangement.startDate) {
        const startDate = new Date(arrangement.startDate)
        const endDate = new Date(arrangement.endDate)
        if (endDate <= startDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be after start date',
            path: ['contractualArrangements', index, 'endDate'],
          })
        }
      }
    })
  }
})

interface UserFormProps {
  initialData?: Partial<User> // User includes id field needed for signature operations
  onSubmit: (data: UserFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function UserForm({ initialData, onSubmit, onCancel, isLoading }: UserFormProps) {
  const addressLabels = getAddressLabels()
  const defaultCountry = getDefaultCountry()
  const [editingArrangementIndex, setEditingArrangementIndex] = useState<number | null>(null)
  const [newArrangement, setNewArrangement] = useState<{ type: 'full-time' | 'part-time', startDate: string, endDate?: string } | null>(null)
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null)
  const [signatureError, setSignatureError] = useState<string | null>(null)
  const [signatureUploading, setSignatureUploading] = useState(false)
  
  // Load signature preview from S3 if signatureS3Key exists
  React.useEffect(() => {
    if (initialData?.signatureS3Key && initialData.id) {
      userService.getSignatureUrl(initialData.id)
        .then(url => setSignaturePreview(url))
        .catch(err => {
          console.warn('Failed to load signature:', err)
          setSignaturePreview(null)
        })
    } else {
      setSignaturePreview(null)
    }
  }, [initialData?.signatureS3Key, initialData?.id])

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    getValues,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      username: initialData?.username || '',
      email: initialData?.email || '',
      salutation: initialData?.salutation,
      firstName: initialData?.firstName || '',
      middleName: initialData?.middleName || '',
      lastName: initialData?.lastName || '',
      role: initialData?.role || 'receptionist',
      roleCategory: initialData?.roleCategory || getRoleCategory(initialData?.role || 'receptionist'),
      isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
      twoFactorEnabled: initialData?.twoFactorEnabled !== undefined ? initialData.twoFactorEnabled : !initialData?.id,
      password: '',
      phone: initialData?.phone || '',
      mobile: initialData?.mobile || '',
      address: initialData?.address || {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: defaultCountry,
      },
      hpi: initialData?.hpi || '',
      professionalBodyRegistrationNumber: initialData?.professionalBodyRegistrationNumber || '',
      prescriberIdentifierNumber: initialData?.prescriberIdentifierNumber || '',
      gmsNumber: initialData?.gmsNumber || '',
      // Signature is now stored separately in S3, not in form data
      contractualArrangements: initialData?.contractualArrangements || [],
    },
  })

  const selectedRole = watch('role')
  const twoFactorEnabled = watch('twoFactorEnabled')
  const watchedArrangements = watch('contractualArrangements')
  const [contractualArrangements, setContractualArrangements] = useState<Array<{ type: 'full-time' | 'part-time', startDate: string, endDate?: string }>>(
    initialData?.contractualArrangements || []
  )
  const isClinical = isClinicalRole(selectedRole)
  // Sync local state with form state
  React.useEffect(() => {
    if (watchedArrangements) {
      setContractualArrangements(watchedArrangements)
    }
  }, [watchedArrangements])
  
  // Auto-update roleCategory when role changes
  React.useEffect(() => {
    if (selectedRole) {
      setValue('roleCategory', getRoleCategory(selectedRole))
    }
  }, [selectedRole, setValue])

  // Handle signature file upload to S3
  const handleSignatureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSignatureError('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    const MAX_SIGNATURE_SIZE = 5 * 1024 * 1024 // 5MB
    if (file.size > MAX_SIGNATURE_SIZE) {
      setSignatureError('Signature image must be less than 5MB')
      return
    }

    setSignatureError(null)
    setSignatureUploading(true)

    try {
      // Only upload if we're editing an existing user
      if (!initialData?.id) {
        setSignatureError('Please save the user first, then upload signature')
        setSignatureUploading(false)
        return
      }

      // Resize image to fit signature line dimensions (200px width × 50px height max)
      // This ensures the signature fits properly on prescriptions
      const { resizeImage } = await import('../utils/imageResize')
      
      console.log('[UserForm] Original signature image:', {
        name: file.name,
        size: file.size,
        type: file.type,
      })
      
      const resizedFile = await resizeImage(file, 200, 50, 0.95)
      
      console.log('[UserForm] Resized signature image:', {
        name: resizedFile.name,
        size: resizedFile.size,
        type: resizedFile.type,
        sizeReduction: `${((1 - resizedFile.size / file.size) * 100).toFixed(1)}%`,
      })

      // Upload resized image to S3
      await userService.uploadSignature(initialData.id, resizedFile)
      
      // Refresh signature preview
      const url = await userService.getSignatureUrl(initialData.id)
      setSignaturePreview(url)
    } catch (error) {
      console.error('Error uploading signature:', error)
      setSignatureError('Failed to upload signature image')
    } finally {
      setSignatureUploading(false)
    }
  }

  const handleRemoveSignature = async () => {
    if (!initialData?.id) {
      setSignaturePreview(null)
      return
    }

    try {
      await userService.deleteSignature(initialData.id)
      setSignaturePreview(null)
      setSignatureError(null)
    } catch (error) {
      console.error('Error removing signature:', error)
      setSignatureError('Failed to remove signature')
    }
  }

  const isEditing = !!initialData?.email
  const passwordRequired = !isEditing

  // Get role-specific label for professional body registration number
  const getProfessionalBodyLabel = () => {
    if (selectedRole === 'nurse') {
      return 'An Bord Altranais Registration Number'
    } else if (selectedRole === 'doctor') {
      return 'Medical Council Registration Number'
    }
    return 'Professional Body Registration Number'
  }

  const handleAddArrangement = () => {
    setNewArrangement({ type: 'full-time', startDate: '' })
  }

  const handleSaveNewArrangement = (arrangement: { type: 'full-time' | 'part-time', startDate: string, endDate?: string }) => {
    if (arrangement && arrangement.startDate && arrangement.startDate.trim() !== '') {
      const current = getValues('contractualArrangements') || []
      const updated = [...current, arrangement]
      setContractualArrangements(updated)
      setValue('contractualArrangements', updated, { shouldValidate: true, shouldDirty: true })
      trigger('contractualArrangements')
      setNewArrangement(null)
    }
  }

  const handleCancelNewArrangement = () => {
    setNewArrangement(null)
  }

  const handleEditArrangement = (index: number) => {
    setEditingArrangementIndex(index)
  }

  const handleSaveArrangement = (index: number, arrangement: { type: 'full-time' | 'part-time', startDate: string, endDate?: string }) => {
    const current = getValues('contractualArrangements') || []
    const updated = [...current]
    updated[index] = arrangement
    setContractualArrangements(updated)
    setValue('contractualArrangements', updated, { shouldValidate: true, shouldDirty: true })
    trigger('contractualArrangements')
    setEditingArrangementIndex(null)
  }

  const handleRemoveArrangement = (index: number) => {
    const current = getValues('contractualArrangements') || []
    const updated = [...current]
    updated.splice(index, 1)
    setContractualArrangements(updated)
    setValue('contractualArrangements', updated, { shouldValidate: true, shouldDirty: true })
    trigger('contractualArrangements')
  }

  const handleFormSubmit = async (data: UserFormData) => {
    // Ensure roleCategory is set based on role
    const submitData: UserFormData = {
      ...data,
      roleCategory: getRoleCategory(data.role),
      password: data.password || undefined,
      // Clean up empty strings
      middleName: data.middleName || undefined,
      phone: data.phone || undefined,
      mobile: data.mobile || undefined,
      hpi: data.hpi || undefined,
      professionalBodyRegistrationNumber: data.professionalBodyRegistrationNumber || undefined,
      prescriberIdentifierNumber: data.prescriberIdentifierNumber || undefined,
      gmsNumber: data.gmsNumber || undefined,
      address: data.address && Object.values(data.address).some(v => v) ? data.address : undefined,
      // Signature is uploaded separately via S3, not included in form submission
    }
    await onSubmit(submitData)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="user-form" noValidate>
      {/* Basic Information Section */}
      <div className="form-section">
        <h2>{isEditing ? 'Edit User' : 'Create New User'}</h2>

        <div className="form-group">
          <label htmlFor="username">
            Username <span className="required">*</span>
          </label>
          <input
            id="username"
            type="text"
            {...register('username')}
            className={errors.username ? 'error' : ''}
            aria-invalid={!!errors.username}
            aria-describedby={errors.username ? 'username-error' : undefined}
            autoComplete="username"
          />
          {errors.username && (
            <span id="username-error" className="error-message" role="alert">
              {errors.username.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <input
            id="email"
            type="email"
            {...register('email')}
            className={errors.email ? 'error' : ''}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
            disabled={isEditing}
            autoComplete="email"
          />
          {errors.email && (
            <span id="email-error" className="error-message" role="alert">
              {errors.email.message}
            </span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="salutation">Title</label>
            <select
              id="salutation"
              {...register('salutation')}
              className={errors.salutation ? 'error' : ''}
              aria-invalid={!!errors.salutation}
            >
              <option value="">(None)</option>
              {USER_SALUTATIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="firstName">
              First Name <span className="required">*</span>
            </label>
            <input
              id="firstName"
              type="text"
              {...register('firstName')}
              className={errors.firstName ? 'error' : ''}
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? 'firstName-error' : undefined}
              autoComplete="given-name"
            />
            {errors.firstName && (
              <span id="firstName-error" className="error-message" role="alert">
                {errors.firstName.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="middleName">Middle Name</label>
            <input
              id="middleName"
              type="text"
              {...register('middleName')}
              className={errors.middleName ? 'error' : ''}
              aria-invalid={!!errors.middleName}
              aria-describedby={errors.middleName ? 'middleName-error' : undefined}
              autoComplete="additional-name"
            />
            {errors.middleName && (
              <span id="middleName-error" className="error-message" role="alert">
                {errors.middleName.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">
              Last Name <span className="required">*</span>
            </label>
            <input
              id="lastName"
              type="text"
              {...register('lastName')}
              className={errors.lastName ? 'error' : ''}
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? 'lastName-error' : undefined}
              autoComplete="family-name"
            />
            {errors.lastName && (
              <span id="lastName-error" className="error-message" role="alert">
                {errors.lastName.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="role">
            Role <span className="required">*</span>
          </label>
          <select
            id="role"
            {...register('role')}
            className={errors.role ? 'error' : ''}
            aria-invalid={!!errors.role}
            aria-describedby={errors.role ? 'role-error' : undefined}
          >
            <optgroup label="Clinical">
              <option value="doctor">Doctor</option>
              <option value="nurse">Nurse</option>
              <option value="physician-assistant">Physician Assistant</option>
              <option value="therapist">Therapist</option>
            </optgroup>
            <optgroup label="Administrative">
              <option value="admin">Admin</option>
              <option value="receptionist">Receptionist</option>
              <option value="billing">Billing</option>
              <option value="office-manager">Office Manager</option>
            </optgroup>
            <optgroup label="System">
              <option value="system-admin">System Admin</option>
              <option value="it-support">IT Support</option>
            </optgroup>
          </select>
          {watch('role') && (
            <p className="field-hint">
              Category: {getRoleCategory(watch('role') as UserRole)}
            </p>
          )}
          {errors.role && (
            <span id="role-error" className="error-message" role="alert">
              {errors.role.message}
            </span>
          )}
        </div>
      </div>

      {/* Personal Details Section */}
      <div className="form-section">
        <h3>Personal Details</h3>

        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            type="tel"
            {...register('phone')}
            className={errors.phone ? 'error' : ''}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
            autoComplete="tel"
          />
          {errors.phone && (
            <span id="phone-error" className="error-message" role="alert">
              {errors.phone.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="mobile">Mobile</label>
          <input
            id="mobile"
            type="tel"
            {...register('mobile')}
            className={errors.mobile ? 'error' : ''}
            aria-invalid={!!errors.mobile}
            aria-describedby={errors.mobile ? 'mobile-error' : undefined}
            autoComplete="tel"
          />
          {errors.mobile && (
            <span id="mobile-error" className="error-message" role="alert">
              {errors.mobile.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="address.street">{addressLabels.street}</label>
          <input
            id="address.street"
            type="text"
            {...register('address.street')}
            className={errors.address?.street ? 'error' : ''}
            aria-invalid={!!errors.address?.street}
            autoComplete="street-address"
          />
          {errors.address?.street && (
            <span className="error-message" role="alert">
              {errors.address.street.message}
            </span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.city">{addressLabels.city}</label>
            <input
              id="address.city"
              type="text"
              {...register('address.city')}
              className={errors.address?.city ? 'error' : ''}
              aria-invalid={!!errors.address?.city}
              autoComplete="address-level2"
            />
            {errors.address?.city && (
              <span className="error-message" role="alert">
                {errors.address.city.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="address.state">{addressLabels.state}</label>
            <input
              id="address.state"
              type="text"
              {...register('address.state')}
              className={errors.address?.state ? 'error' : ''}
              aria-invalid={!!errors.address?.state}
              autoComplete="address-level1"
            />
            {errors.address?.state && (
              <span className="error-message" role="alert">
                {errors.address.state.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="address.postalCode">{addressLabels.postalCode}</label>
            <input
              id="address.postalCode"
              type="text"
              {...register('address.postalCode')}
              className={errors.address?.postalCode ? 'error' : ''}
              aria-invalid={!!errors.address?.postalCode}
              autoComplete="postal-code"
            />
            {errors.address?.postalCode && (
              <span className="error-message" role="alert">
                {errors.address.postalCode.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="address.country">{addressLabels.country}</label>
            <input
              id="address.country"
              type="text"
              {...register('address.country')}
              className={errors.address?.country ? 'error' : ''}
              aria-invalid={!!errors.address?.country}
              autoComplete="country"
            />
            {errors.address?.country && (
              <span className="error-message" role="alert">
                {errors.address.country.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Professional Information Section */}
      {isClinical && (
        <div className="form-section">
          <h3>Professional Information</h3>

          <div className="form-group">
            <label htmlFor="hpi">Healthcare Practitioner Identifier (HPI)</label>
            <input
              id="hpi"
              type="text"
              {...register('hpi')}
              className={errors.hpi ? 'error' : ''}
              aria-invalid={!!errors.hpi}
              aria-describedby={errors.hpi ? 'hpi-error' : undefined}
            />
            {errors.hpi && (
              <span id="hpi-error" className="error-message" role="alert">
                {errors.hpi.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="professionalBodyRegistrationNumber">
              {getProfessionalBodyLabel()} <span className="required">*</span>
            </label>
            <input
              id="professionalBodyRegistrationNumber"
              type="text"
              {...register('professionalBodyRegistrationNumber')}
              className={errors.professionalBodyRegistrationNumber ? 'error' : ''}
              aria-invalid={!!errors.professionalBodyRegistrationNumber}
              aria-describedby={errors.professionalBodyRegistrationNumber ? 'professionalBodyRegistrationNumber-error' : undefined}
            />
            {errors.professionalBodyRegistrationNumber && (
              <span id="professionalBodyRegistrationNumber-error" className="error-message" role="alert">
                {errors.professionalBodyRegistrationNumber.message}
              </span>
            )}
          </div>

          {selectedRole === 'nurse' && (
            <div className="form-group">
              <label htmlFor="prescriberIdentifierNumber">
                Prescriber Identifier Number <span className="required">*</span>
              </label>
              <input
                id="prescriberIdentifierNumber"
                type="text"
                {...register('prescriberIdentifierNumber')}
                className={errors.prescriberIdentifierNumber ? 'error' : ''}
                aria-invalid={!!errors.prescriberIdentifierNumber}
                aria-describedby={errors.prescriberIdentifierNumber ? 'prescriberIdentifierNumber-error' : undefined}
              />
              {errors.prescriberIdentifierNumber && (
                <span id="prescriberIdentifierNumber-error" className="error-message" role="alert">
                  {errors.prescriberIdentifierNumber.message}
                </span>
              )}
            </div>
          )}

          {selectedRole === 'doctor' && (
            <div className="form-group">
              <label htmlFor="gmsNumber">
                Doctor's GMS Number <span className="required">*</span>
              </label>
              <input
                id="gmsNumber"
                type="text"
                {...register('gmsNumber')}
                className={errors.gmsNumber ? 'error' : ''}
                aria-invalid={!!errors.gmsNumber}
                aria-describedby={errors.gmsNumber ? 'gmsNumber-error' : undefined}
              />
              {errors.gmsNumber && (
                <span id="gmsNumber-error" className="error-message" role="alert">
                  {errors.gmsNumber.message}
                </span>
              )}
            </div>
          )}

          {/* Signature Upload */}
          <div className="form-group">
            <label htmlFor="signature">
              Signature
            </label>
            <div className="signature-upload-container">
              {signaturePreview ? (
                <div className="signature-preview">
                  <img src={signaturePreview} alt="Signature preview" className="signature-image" />
                  <div className="signature-actions">
                    <label htmlFor="signature" className="btn-secondary btn-small" style={{ opacity: signatureUploading ? 0.5 : 1 }}>
                      {signatureUploading ? 'Uploading...' : 'Change Signature'}
                    </label>
                    <button
                      type="button"
                      onClick={handleRemoveSignature}
                      className="btn-secondary btn-small"
                      disabled={signatureUploading}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="signature-upload-placeholder">
                  <label htmlFor="signature" className="signature-upload-label">
                    <span className="upload-icon">📝</span>
                    <span>Click to upload signature image</span>
                    <span className="hint">PNG, JPG, or GIF (max 5MB)</span>
                  </label>
                </div>
              )}
              <input
                id="signature"
                type="file"
                accept="image/*"
                onChange={handleSignatureUpload}
                className="signature-input"
                style={{ display: 'none' }}
                disabled={signatureUploading || !initialData?.id}
              />
              {signatureError && (
                <span className="error-message" role="alert">
                  {signatureError}
                </span>
              )}
            </div>
            <p className="form-hint">Signature will be used to sign prescriptions and other documents</p>
          </div>
        </div>
      )}

      {/* Contractual Arrangements Section */}
      <div className="form-section">
        <h3>Contractual Arrangements</h3>
        {errors.contractualArrangements && (
          <span className="error-message" role="alert">
            {errors.contractualArrangements.message}
          </span>
        )}

        {contractualArrangements && contractualArrangements.length > 0 && (
          <div className="arrangements-list">
            {contractualArrangements.map((arrangement, index) => (
              <div key={`arrangement-${index}-${arrangement.startDate}`} className="arrangement-item">
                {editingArrangementIndex === index ? (
                  <ArrangementEditForm
                    arrangement={arrangement}
                    onSave={(updated) => handleSaveArrangement(index, updated)}
                    onCancel={() => setEditingArrangementIndex(null)}
                    errors={errors.contractualArrangements?.[index]}
                  />
                ) : (
                  <div className="arrangement-display">
                    <div>
                      <strong>{arrangement.type === 'full-time' ? 'Full-time' : 'Part-time'}</strong>
                      <span> - Start: {new Date(arrangement.startDate).toLocaleDateString()}</span>
                      {arrangement.endDate && (
                        <span> - End: {new Date(arrangement.endDate).toLocaleDateString()}</span>
                      )}
                    </div>
                    <div className="arrangement-actions">
                      <button
                        type="button"
                        onClick={() => handleEditArrangement(index)}
                        className="btn-link"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveArrangement(index)}
                        className="btn-link"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {newArrangement ? (
          <ArrangementEditForm
            arrangement={newArrangement}
            onSave={handleSaveNewArrangement}
            onCancel={handleCancelNewArrangement}
          />
        ) : (
          <button
            type="button"
            onClick={handleAddArrangement}
            className="btn-secondary"
          >
            + Add Arrangement
          </button>
        )}
      </div>

      {/* Account Settings Section */}
      <div className="form-section">
        <h3>Account Settings</h3>

        <div className="two-factor-status-row">
          <span className="two-factor-status-label">2FA Status</span>
          {twoFactorEnabled ? (
            <span className="two-factor-badge two-factor-badge-enabled">Enabled</span>
          ) : (
            <span className="two-factor-badge two-factor-badge-disabled">Disabled</span>
          )}
        </div>

        <div className="two-factor-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setValue('twoFactorEnabled', true, { shouldValidate: true, shouldDirty: true })}
            disabled={twoFactorEnabled === true}
            aria-label="Enable two-factor authentication"
          >
            Enable 2FA
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setValue('twoFactorEnabled', false, { shouldValidate: true, shouldDirty: true })}
            disabled={twoFactorEnabled === false}
            aria-label="Disable two-factor authentication"
          >
            Disable 2FA
          </button>
        </div>
        <p className="form-hint">
          2FA sends a one-time verification code to the user&apos;s registered email on login.
        </p>

        <div className="form-group">
          <label htmlFor="password">
            {isEditing ? 'New Password (leave blank to keep current)' : 'Password'} {!isEditing && <span className="required">*</span>}
          </label>
          <input
            id="password"
            type="password"
            {...register('password', { required: passwordRequired ? 'Password is required' : false })}
            className={errors.password ? 'error' : ''}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
            autoComplete={isEditing ? 'new-password' : 'new-password'}
          />
          {errors.password && (
            <span id="password-error" className="error-message" role="alert">
              {errors.password.message}
            </span>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input type="checkbox" {...register('isActive')} />
            <span>Active</span>
          </label>
          <p className="form-hint">Inactive users cannot log in to the system</p>
        </div>

      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : isEditing ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  )
}

// Helper component for editing arrangements
interface ArrangementEditFormProps {
  arrangement: { type: 'full-time' | 'part-time', startDate: string, endDate?: string }
  onSave: (arrangement: { type: 'full-time' | 'part-time', startDate: string, endDate?: string }) => void
  onCancel: () => void
  errors?: any
}

function ArrangementEditForm({ arrangement, onSave, onCancel, errors }: ArrangementEditFormProps) {
  const [type, setType] = useState<'full-time' | 'part-time'>(arrangement.type)
  const [startDate, setStartDate] = useState(arrangement.startDate)
  const [endDate, setEndDate] = useState(arrangement.endDate || '')

  // Sync local state with prop when arrangement changes
  React.useEffect(() => {
    setType(arrangement.type)
    setStartDate(arrangement.startDate)
    setEndDate(arrangement.endDate || '')
  }, [arrangement.type, arrangement.startDate, arrangement.endDate])

  const handleSave = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    if (startDate && startDate.trim() !== '') {
      onSave({
        type,
        startDate,
        endDate: endDate && endDate.trim() !== '' ? endDate : undefined,
      })
    } else {
      // Show validation error if start date is missing
      alert('Start date is required')
    }
  }

  return (
    <div className="arrangement-edit-form">
      <div className="form-row-3">
        <div className="form-group">
          <label>Type</label>
          <select value={type} onChange={(e) => setType(e.target.value as 'full-time' | 'part-time')}>
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time</option>
          </select>
        </div>
        <div className="form-group">
          <label>
            Start Date <span className="required">*</span>
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={errors?.startDate ? 'error' : ''}
          />
          {errors?.startDate && (
            <span className="error-message">{errors.startDate.message}</span>
          )}
        </div>
        <div className="form-group">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={errors?.endDate ? 'error' : ''}
          />
          {errors?.endDate && (
            <span className="error-message">{errors.endDate.message}</span>
          )}
        </div>
      </div>
      <div className="arrangement-actions">
        <button type="button" onClick={handleSave} className="btn-primary">
          Save
        </button>
        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }} className="btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  )
}


